import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CAROUSEL_ITEM_MARGIN, RANK_TRACKER_ID, widgetItems } from "app/pages/activation/components/RankWidget/config";
import { CreationModal } from "app/pages/activation/components/RankWidget/CreationModal/CreationModal";
import { creationModalKeys, widgetKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { widgetTexts } from "app/pages/activation/components/RankWidget/texts";
import { useRTCreationModal } from "app/pages/activation/hooks/rankTracker/useRTCreationModal";
import { useRTWidgetData } from "app/pages/activation/hooks/rankTracker/useRTWidgetData";
import { Carousel } from "pro-features/components/Carousel/src/Carousel";

import { Header } from "./Header/Header";
import * as SC from "./styles";
import { WidgetItem } from "./WidgetItem/WidgetItem";

type RankWidgetSchema = {
    isViewerMode: boolean;
};

export const RankTrackerWidget = ({ isViewerMode }: RankWidgetSchema) => {
    const {
        params: { shouldScrollToRT },
    } = useCurrentStateAndParams<{
        shouldScrollToRT?: boolean;
    }>();

    const {
        project,
        RTCampaignData,
        KeywordsTrendData,
        isRTCampaignLoading,
        KeywordsTrendsIsLoading,
        isWidgetDisabled,
        isScraping,
        isTopicsExists,
        isTooltipEnabled,
        buttonText,
        buttonTooltip,
        isWidgetAllowed,
    } = useRTWidgetData(isViewerMode);

    const {
        isCreationOpen,
        onOpenCreation,
        onCloseCreation,
        domain,
        competitors,
        country,
        projectName,
        projectId,
        availableCountries,
        isCountriesLoading,
    } = useRTCreationModal(project);

    useEffect(() => {
        if (shouldScrollToRT) {
            document.getElementById(RANK_TRACKER_ID)?.scrollIntoView({ behavior: "smooth", inline: "end" });
        }
    }, [shouldScrollToRT]);

    if (!isWidgetAllowed) {
        return null;
    }

    return (
        <>
            <SC.GlobalStyles />
            <CreationModal
                isOpen={isCreationOpen}
                onClose={onCloseCreation}
                domain={domain}
                competitors={competitors}
                projectName={projectName}
                projectId={projectId}
                dataAutomation={creationModalKeys.container}
                country={country}
                availableCountries={availableCountries}
                isCountriesLoading={isCountriesLoading}
            />

            <SC.Container id={RANK_TRACKER_ID}>
                {!isWidgetDisabled ? (
                    <Header
                        isScraping={isScraping}
                        RTCampaign={RTCampaignData}
                    />
                ) : null}

                <SC.CarouselWrapper data-automation={widgetKeys.carousel}>
                    {isWidgetDisabled ? (
                        <SC.CarouselHeader data-automation={widgetKeys.carouselHeader}>
                            <SWReactIcons
                                iconName="bars"
                                iconColor={colorsPalettes.carbon[200]}
                                size="xs"
                            />

                            {translate(widgetTexts.header)}
                        </SC.CarouselHeader>
                    ) : null}

                    <Carousel margin={CAROUSEL_ITEM_MARGIN}>
                        {widgetItems.map((item) => (
                            <WidgetItem
                                key={item.name}
                                {...item}
                                change={isWidgetDisabled || isScraping ? null : KeywordsTrendData?.[item.name]}
                                isScraping={isScraping}
                                isLoading={isRTCampaignLoading || KeywordsTrendsIsLoading}
                                RTCampaign={RTCampaignData}
                            />
                        ))}
                    </Carousel>

                    <SC.CarouselDisabledContainer isDisabled={isWidgetDisabled} />

                    {isWidgetDisabled ? (
                        <SC.CreateButtonContainer>
                            <Tooltip
                                className="sw-tooltip"
                                content={buttonTooltip}
                                enabled={isTooltipEnabled}
                            >
                                <Button
                                    isDisabled={!isTopicsExists || isViewerMode}
                                    onClick={onOpenCreation}
                                >
                                    {buttonText}
                                </Button>
                            </Tooltip>
                        </SC.CreateButtonContainer>
                    ) : null}
                </SC.CarouselWrapper>
            </SC.Container>
        </>
    );
};
